import React from 'react'
import Link from 'redux-first-router-link'
import { t } from 'ttag'
import cx from 'classnames'

import { useDispatch } from 'react-redux'
import Pane from 'components/common/Pane'
import Icon from 'components/common/Icon'
import { Icon as SharedIcon } from 'components/shared/Icon/Icon'
import {
  AlertTriangleIcon,
  BookOpenIcon,
  CheckIcon,
  FileTextIcon,
  HelpCircleIcon,
  ListIcon,
  MessageCircleIcon,
  SlidersIcon,
  UserCheckIcon,
} from 'components/shared/Icon/model'
import ReactMarkdown from 'react-markdown'
import type { Components } from 'react-markdown'
import { titleCase } from 'utils/stringFormat'
import { useLanguage, useLocation } from 'hooks'
import { toPage } from 'ducks/router'
import markdown from './markdown/'
import Illustration from 'components/common/Illustration'
import { SentryErrorBoundary } from 'components/common/SentryErrorBoundary'
import { Accordion } from 'components/shared/Accordion/Accordion'
import {
  buildHelpPageSections,
  getPageFeatures,
  hasPinnedImages,
  PERSONALITY_FACTORS_SECTION_TITLE,
} from './helpContent'
import HelpBigFiveTraits from './HelpBigFiveTraits'
import HelpHardinessCards from './HelpHardinessCards'
import HelpJobProfileSkills from './HelpJobProfileSkills'
import { BIG_FIVE_SECTION_TITLE } from './bigFiveTraitsData'
import { SKILLS_SECTION_TITLE } from './jobProfileSkillsData'

const HELP_INDEX_ICONS = {
  1: SlidersIcon,
  2: HelpCircleIcon,
  3: FileTextIcon,
  4: MessageCircleIcon,
  5: ListIcon,
  6: UserCheckIcon,
  7: AlertTriangleIcon,
}

const MARKDOWN_COMPONENTS: Components = {
  img: ({ src, ...props }: any) => (
    <SentryErrorBoundary>
      <Illustration
        name={src.match(/\/(?:undraw_)?([^/]*)\.(?:svg|png)$/)[1]}
        {...props}
      />
    </SentryErrorBoundary>
  ),
  a: ({ href, children, ...props }: any) =>
    href.match(/^[./]/) ? (
      <Link to={href} {...props} children={children} />
    ) : (
      <a className="external-link" href={href} children={children} {...props} />
    ),
  p: ({ children, ...props }: any) =>
    React.Children.toArray(children).some(
      child => typeof child === 'string',
    ) ? (
      <p {...props}>{children}</p>
    ) : (
      <>{children}</>
    ),
}

const HelpMarkdown = ({ children }) => (
  <ReactMarkdown children={children} components={MARKDOWN_COMPONENTS} />
)

const toHelpTab = (tab = 0) => toPage('training', tab)

const getAdjacentHelpPages = (pages, currentTab) => {
  const orderedPages = pages.filter(Boolean).sort((a, b) => a.tab - b.tab)
  const currentIndex = orderedPages.findIndex(page => page.tab === currentTab)

  if (currentIndex === -1) return { prev: null, next: null }

  return {
    prev: orderedPages[currentIndex - 1] ?? null,
    next: orderedPages[currentIndex + 1] ?? null,
  }
}

const HelpStepper = ({ current = 0, pages }) => {
  const orderedPages = pages.filter(Boolean).sort((a, b) => a.tab - b.tab)
  const intro = orderedPages.find(page => page.tab === 0)
  const steps = orderedPages.filter(page => page.tab > 0)

  return (
    <div
      role="navigation"
      className="HelpStepper"
      aria-label={t`Help sections`}
    >
      {intro && (
        <Link
          to={toHelpTab(intro.tab)}
          className={cx('HelpStepper__intro', {
            'HelpStepper__intro--active': current === intro.tab,
          })}
          aria-current={current === intro.tab ? 'page' : undefined}
        >
          <span className="HelpStepper__indicator">
            <SharedIcon icon={BookOpenIcon} size={14} />
          </span>
          <span className="HelpStepper__label">{titleCase(intro.label)}</span>
        </Link>
      )}
      <span className="HelpStepper__divider" />
      <div className="HelpStepper__steps">
        {steps.map(({ tab, label }) => {
          const isCompleted = tab < current
          const isActive = tab === current

          return (
            <Link
              key={tab}
              to={toHelpTab(tab)}
              className={cx('HelpStepper__step', {
                'HelpStepper__step--completed': isCompleted,
                'HelpStepper__step--active': isActive,
              })}
              aria-current={isActive ? 'step' : undefined}
            >
              <span className="HelpStepper__indicator">
                {isCompleted ? <SharedIcon icon={CheckIcon} size={14} /> : tab}
              </span>
              <span className="HelpStepper__label">{titleCase(label)}</span>
            </Link>
          )
        })}
      </div>
    </div>
  )
}

const LinkToPage = ({ label, tab }) => (
  <Link to={toHelpTab(tab)}>{titleCase(label)}</Link>
)

const HelpFooter = ({ next, prev }) => (
  <div className="HelpFooter">
    {prev && (
      <span className="prev">
        <Icon name="chevronLeft" />
        <LinkToPage {...prev} />
      </span>
    )}
    {next && (
      <span className="next">
        <LinkToPage {...next} />
        <Icon name="chevronRight" />
      </span>
    )}
  </div>
)

const IndexCard = ({ tab, title, sections }) => {
  const cardIcon = HELP_INDEX_ICONS[tab]

  return (
    <Link to={toHelpTab(tab)} className="HelpIndexCard">
      <div className="HelpIndexCard__header">
        <div className="HelpIndexCard__icon">
          {cardIcon ? <SharedIcon icon={cardIcon} size={20} /> : null}
        </div>
        <span className="HelpIndexCard__number">
          {String(tab).padStart(2, '0')}
        </span>
      </div>
      <h2 className="HelpIndexCard__title">{title}</h2>
      {sections.length > 0 ? (
        <ul className="HelpIndexCard__sections">
          {sections.map((txt, idx) => (
            <li key={idx}>{txt}</li>
          ))}
        </ul>
      ) : null}
    </Link>
  )
}

const HelpIndex = ({ pages }) => (
  <div className="HelpIndex">
    {pages.map(props =>
      props.tab === 0 ? null : <IndexCard key={props.tab} {...props} />,
    )}
  </div>
)

const HelpSectionAccordion = ({
  title,
  children,
  isInitiallyOpen = false,
  className,
}) => (
  <Accordion
    className={cx('HelpAccordion', className)}
    isInitiallyOpen={isInitiallyOpen}
  >
    <Accordion.Header
      title={title}
      buttonClassName="HelpAccordion__button"
      wrapperClassName="HelpAccordion__title"
    />
    <Accordion.Panel className="HelpAccordion__panel">
      {children}
    </Accordion.Panel>
  </Accordion>
)

const renderAccordionExtras = (section, features, language) => {
  const { title } = section

  if (features.bigFiveTraits && title === BIG_FIVE_SECTION_TITLE[language]) {
    return <HelpBigFiveTraits language={language} />
  }

  if (features.skillCards && title === SKILLS_SECTION_TITLE[language]) {
    return <HelpJobProfileSkills language={language} />
  }

  return null
}

const renderAccordionBody = (section, features, language) => {
  if (section.bodyBefore != null && section.bodyAfter != null) {
    return (
      <>
        <HelpMarkdown children={section.bodyBefore} />
        <HelpHardinessCards language={language} />
        <HelpMarkdown children={section.bodyAfter} />
      </>
    )
  }

  const extras = renderAccordionExtras(section, features, language)
  const body = <HelpMarkdown children={section.body} />

  if (section.sideImage) {
    return (
      <div className="HelpAccordionSplit">
        <div className="HelpAccordionSplit__text">
          {body}
          {extras}
        </div>
        <div className="HelpAccordionSplit__image">
          <HelpMarkdown children={section.sideImage} />
        </div>
      </div>
    )
  }

  return (
    <>
      {body}
      {extras}
    </>
  )
}

const HelpPageContent = ({ content, language, label }) => {
  const { features, pinnedImages, sections } = buildHelpPageSections(
    content,
    language,
    label,
  )

  const firstAccordionIndex = sections.findIndex(
    section => section.kind === 'accordion',
  )

  const main = sections.map((section, index) => {
    if (section.kind === 'markdown') {
      return <HelpMarkdown key={index} children={section.content} />
    }

    return (
      <React.Fragment key={index}>
        <HelpSectionAccordion
          title={section.title}
          isInitiallyOpen={index === firstAccordionIndex}
          className={
            section.title === PERSONALITY_FACTORS_SECTION_TITLE[language]
              ? 'HelpAccordion--personality-factors'
              : undefined
          }
        >
          {renderAccordionBody(section, features, language)}
        </HelpSectionAccordion>
        {section.summary ? <HelpMarkdown children={section.summary} /> : null}
      </React.Fragment>
    )
  })

  if (!features.pinImages || !pinnedImages.length) {
    return main
  }

  return (
    <>
      <div className="HelpPage__content">{main}</div>
      <aside className="HelpPage__pinnedIllustration">
        {pinnedImages.map((image, index) => (
          <HelpMarkdown key={index} children={image} />
        ))}
      </aside>
    </>
  )
}

const HelpPage = () => {
  const location = useLocation()
  const dispatch = useDispatch()
  const tab = Number.parseInt(location?.payload?.tab ?? 0, 10) || 0
  const language = useLanguage()
  const pages = markdown[language ?? ''] || []
  const page = pages[tab]
  const contentRef = React.useRef<HTMLDivElement>(null)

  // the current tab may not exist after a language switch
  // (languages have different page counts) - land at the introduction
  React.useEffect(() => {
    if (!page && pages.length) dispatch(toHelpTab(0))
  }, [page, pages.length, dispatch])

  React.useEffect(() => {
    for (
      let el: HTMLElement | null = contentRef.current;
      el;
      el = el.parentElement
    ) {
      if (el.scrollTop > 0) el.scrollTop = 0
    }
  }, [tab])

  if (!page) return '...'

  const { prev, next } = getAdjacentHelpPages(pages, tab)
  const pageFeatures = getPageFeatures(page.label)

  return (
    <Pane>
      <HelpStepper current={tab} pages={pages} />
      <div
        ref={contentRef}
        className={cx('HelpPage', {
          'HelpPage--intro': tab === 0,
          'HelpPage--pinned-image': hasPinnedImages(page.label),
          'HelpPage--wide-accordions': Boolean(pageFeatures.wideAccordions),
          'HelpPage--wide-summary': Boolean(pageFeatures.wideSummary),
          'HelpPage--wide-sections': Boolean(pageFeatures.wideSections),
        })}
      >
        <header className="HelpPage__heading">
          <h1>{titleCase(page.label)}</h1>
          <p className="HelpPage__subheading">{page.title}</p>
        </header>
        <HelpPageContent
          content={page.content}
          language={language}
          label={page.label}
        />
        {tab ? null : <HelpIndex pages={pages} />}
        <HelpFooter next={next} prev={prev} />
      </div>
    </Pane>
  )
}

export default HelpPage
